import { useState } from "react";
import { Link } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormTitle, InputWithLabel } from "@ptsd/uikit";
import { ArrowLeft } from "@tamagui/lucide-icons";
import i18next from "i18next";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Form, Separator, Text, useMedia, View, XStack } from "tamagui";
import z from "zod";

import { authClient } from "~/utils/auth";
import img from "~/assets/signup/signup-4.png";
import {
  AuthBaseForm,
  AuthContainer,
  AuthContent,
  AuthImage,
} from "~/component/feature/auth/wrappers";

export default function ForgotPasswordPage() {
  return (
    <AuthContainer>
      <AuthContent>
        <ForgotPasswordForm />
      </AuthContent>

      <AuthImage src={img} />
    </AuthContainer>
  );
}

export const schema = z.object({
  email: z
    .string()
    .nonempty(i18next.t("signup:validation.required"))
    .email(i18next.t("signup:validation.email.invalidFormat")),
});

export type ForgotPasswordFormData = z.infer<typeof schema>;

const ForgotPasswordForm = () => {
  const { t } = useTranslation("forgotpassword");
  const { sm } = useMedia();

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const { setError, control, getValues, handleSubmit, reset } =
    useForm<ForgotPasswordFormData>({
      defaultValues: { email: "" },
      resolver: zodResolver(schema),
    });

  const onSubmit = (data: z.infer<typeof schema>) => {
    if (isLoading || isSuccess) return;
    setIsSuccess(false);
    setIsLoading(true);
    authClient
      .requestPasswordReset({
        email: data.email,
        redirectTo: "/auth/reset-password",
      })
      .then((result) => {
        if (result.error) setError("email", { message: t(`error`) });
        if (result.data?.status === true) setIsSuccess(true);
      })
      .catch(() => setError("email", { message: t(`error`) }))
      .finally(() => setIsLoading(false));
  };

  return (
    <AuthBaseForm onSubmit={handleSubmit(onSubmit)}>
      <View width={"100%"} gap={"$5"}>
        {isSuccess && (
          <View gap={"$3"}>
            <View gap={"$2"}>
              <FormTitle>
                Ми надіслали лист на вашу електронну пошту {getValues("email")}
              </FormTitle>

              <Text
                fontFamily={"$subtitle"}
                fontWeight={"$5"}
                fontSize={"$4"}
                lineHeight={"$4"}
              >
                Перейдіть за посиланням у листі, щоб скинути пароль
              </Text>
            </View>
            <Separator />
            <Button
              padding={0}
              height={"auto"}
              justifyContent="flex-start"
              variant={"transparent"}
              focusStyle={{
                backgroundColor: "transparent",
                borderWidth: 0,
              }}
              borderColor="transparent"
              hoverStyle={{
                backgroundColor: "transparent",
              }}
              pressStyle={{
                backgroundColor: "transparent",
              }}
              onPress={() => {
                reset();
                setIsSuccess((prev) => !prev);
              }}
            >
              <Text
                fontFamily={"$body"}
                fontWeight={"$5"}
                fontSize={"$3"}
                lineHeight={"$3"}
                color={"$lightblue-700"}
              >
                Я не отримав(-ла) листа, надіслати новий
              </Text>
            </Button>
          </View>
        )}
        {!isSuccess && (
          <>
            <FormTitle>{t("ui.form.title")}</FormTitle>
            <View width={"100%"} gap={"$2"}>
              <View width={"100%"} gap={"$4"}>
                <Controller
                  control={control}
                  name={"email"}
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState }) => {
                    return (
                      <InputWithLabel
                        data-id={"forgot-password-email"}
                        variant="inline"
                        size="md"
                        isError={!!fieldState.error?.message}
                        error={fieldState.error?.message}
                        label={t("ui.form.email.label")}
                        placeholder={t("ui.form.email.placeholder")}
                        onChangeText={(text) => field.onChange(text)}
                        {...field}
                      />
                    );
                  }}
                />
              </View>
            </View>
          </>
        )}
      </View>

      <XStack gap={"$0.5"}>
        <Link data-id={"forgot-password-back"} href="/auth" asChild>
          <Button
            variant="gray"
            size={sm ? "md" : "lg"}
            mode="icon"
            rounded="lg"
          >
            <ArrowLeft color={"$gray-primary-900"} size={"$2.5"} />
          </Button>
        </Link>

        {!isSuccess && (
          <Form.Trigger asChild>
            <Button
              data-id={"forgot-password-submit"}
              flex={1}
              variant="primary"
              size={sm ? "md" : "lg"}
              rounded="lg"
            >
              {isLoading
                ? t("ui.form.actions.submit.loading")
                : t("ui.form.actions.submit.label")}
            </Button>
          </Form.Trigger>
        )}
      </XStack>
    </AuthBaseForm>
  );
};
